import {
  PortedTicketData,
  PortedCartItem,
  PortedCategory,
  PortedDiscountLine,
} from './types';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * ADAPTATEUR — objet ticket de la BORNE → forme neutre des modules portés
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ C'EST LE FICHIER À LIRE EN PREMIER en cas d'écart de montant. Les modules portés
 * n'inventent aucun chiffre : tout ce qu'ils impriment sort d'ici.
 *
 * Trois conversions réelles, et rien d'autre :
 *
 * 1. CATÉGORIE. Borne : `item.menuItem.category` (un identifiant) + `Category.name` est
 *    un TABLEAU multilingue + l'ordre vient de `sequence_number`. Console : `item.categoryId`
 *    + `name` chaîne + `sequence`. On convertit, on ne devine pas : un article dont la
 *    catégorie est inconnue tombe dans un groupe « Autres » placé en fin de ticket, il
 *    n'est JAMAIS perdu.
 *
 * 2. REMISES. Borne : `loyaltyDiscount` / `happyHourDiscount` sont à la RACINE de
 *    l'objet et NE SONT PAS déduites de `taxResult.totalTTC` (les formateurs historiques
 *    font la soustraction eux-mêmes, cf. `OrderConfirmation.buildProfessionalTicket`).
 *    Console : les modules attendent un `totalTTC` DÉJÀ NET. On fait donc la soustraction
 *    ICI, une seule fois, et on la borne à 0.
 *
 * 3. TVA. `subtotalHT` / `taxAmount` / `taxRate` sont recopiés TELS QUELS depuis
 *    `services/ticketTaxService` : ils portent sur la base AVANT remise, ce qui est
 *    cohérent avec les lignes d'articles imprimées (prix catalogue). On ne recalcule
 *    JAMAIS le HT à partir du TTC quand la borne l'a déjà fourni — un écart d'un centime
 *    rendrait le ticket non conforme.
 */

/** Forme large et tolérante de ce que la borne passe aux formateurs. */
export interface KioskTicketInput {
  storeInfo?: { name?: string; address?: string; phone?: string; [k: string]: unknown } | null;
  orderCode?: string | null;
  items?: Array<{
    menuItem?: { name?: string; price?: number; category?: string };
    quantity?: number;
    customizations?: Record<string, Array<{ name?: string; price?: number }>>;
    note?: string;
  }>;
  categories?: Array<{ _id?: string; name?: string[] | string; sequence_number?: number | string }>;
  orderType?: 'takeaway' | 'dine-in' | 'delivery';
  tableNumber?: string;
  customerName?: string;
  taxResult?: {
    subtotalHT?: number;
    taxAmount?: number;
    taxRate?: number | null;
    totalTTC?: number;
  } | null;
  orderTotals?: { subtotal?: number; taxAmount?: number; invoiceTotal?: number };
  paymentMethod?: 'cash' | 'card' | string;
  profileSettings?: { terminalName?: string; siretMessage?: string; tvaMessage?: string };
  taxSettings?: { displayMode?: string; showCategories?: boolean; showItemTaxDetail?: boolean };
  loyaltyDiscount?: number;
  loyaltyPointsUsed?: number;
  happyHourDiscount?: number;
  happyHourName?: string;
  happyHourPercent?: number;
  orderDate?: string | Date;
  [k: string]: unknown;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (v: unknown): number => Math.round(num(v) * 100) / 100;

/** `name` de la borne est un TABLEAU multilingue : on prend la première entrée non vide. */
const flattenName = (name: unknown): string => {
  if (Array.isArray(name)) {
    const found = name.find((n) => !!n && String(n).trim() !== '');
    return found ? String(found) : '';
  }
  return name ? String(name) : '';
};

const toPortedItems = (items: KioskTicketInput['items']): PortedCartItem[] =>
  (Array.isArray(items) ? items : []).map((item) => {
    const customizations: Record<string, Array<{ name: string; price: number }>> = {};
    const raw = item?.customizations || {};
    for (const key of Object.keys(raw)) {
      const list = Array.isArray(raw[key]) ? raw[key] : [];
      customizations[key] = list.map((opt) => ({
        name: String(opt?.name ?? ''),
        price: num(opt?.price),
      }));
    }

    return {
      menuItem: {
        name: String(item?.menuItem?.name ?? ''),
        price: num(item?.menuItem?.price),
      },
      quantity: num(item?.quantity) || 1,
      customizations,
      note: item?.note,
      // ⚠️ LA conversion clé : la borne porte la catégorie sur le PRODUIT.
      categoryId: item?.menuItem?.category || undefined,
    };
  });

const toPortedCategories = (categories: KioskTicketInput['categories']): PortedCategory[] =>
  (Array.isArray(categories) ? categories : [])
    .filter((cat) => !!cat?._id)
    .map((cat, index) => ({
      _id: String(cat._id),
      name: flattenName(cat.name),
      // Repli sur l'ordre du tableau : la borne renvoie déjà les catégories triées.
      sequence: Number.isFinite(Number(cat.sequence_number))
        ? Number(cat.sequence_number)
        : index,
    }));

export const toPortedTicketData = (input: KioskTicketInput): PortedTicketData => {
  const loyaltyDiscount = round2(input.loyaltyDiscount);
  const loyaltyPointsUsed = num(input.loyaltyPointsUsed);
  const happyHourDiscount = round2(input.happyHourDiscount);

  const grossTTC = round2(input.taxResult?.totalTTC ?? input.orderTotals?.invoiceTotal ?? 0);
  // Total RÉELLEMENT DÛ. Borné à 0 : une remise supérieure au panier ne doit jamais
  // produire un total négatif sur un document fiscal.
  const netTTC = Math.max(0, round2(grossTTC - loyaltyDiscount - happyHourDiscount));

  const extraDiscountLines: PortedDiscountLine[] = [];
  if (happyHourDiscount > 0) {
    const percent = num(input.happyHourPercent);
    const name = String(input.happyHourName || '').trim();
    // Libellé issu du SERVEUR quand il existe ; sinon « Happy Hour -20% ».
    extraDiscountLines.push({
      label: name || (percent > 0 ? `Happy Hour -${percent}%` : 'Happy Hour'),
      amount: happyHourDiscount,
    });
  }

  const hasTaxResult =
    !!input.taxResult &&
    input.taxResult.subtotalHT !== undefined &&
    input.taxResult.taxAmount !== undefined;

  return {
    storeInfo: input.storeInfo || undefined,
    orderCode: input.orderCode ?? null,
    orderDate: input.orderDate ?? new Date(),

    items: toPortedItems(input.items),
    categories: toPortedCategories(input.categories),

    orderType: input.orderType || 'dine-in',
    tableNumber: input.tableNumber,
    customerName: input.customerName,

    taxResult: hasTaxResult
      ? {
          subtotalHT: num(input.taxResult?.subtotalHT),
          taxAmount: num(input.taxResult?.taxAmount),
          taxRate: input.taxResult?.taxRate ?? null,
          totalTTC: netTTC,
        }
      : undefined,

    orderTotals: {
      subtotal: num(input.orderTotals?.subtotal),
      taxAmount: num(input.orderTotals?.taxAmount),
      invoiceTotal: netTTC,
      loyaltyDiscount: loyaltyDiscount > 0 ? loyaltyDiscount : undefined,
      loyaltyPointsUsed: loyaltyPointsUsed > 0 ? loyaltyPointsUsed : undefined,
    },
    total: netTTC,
    extraDiscountLines: extraDiscountLines.length ? extraDiscountLines : undefined,

    paymentMethod: input.paymentMethod,
    profileSettings: input.profileSettings,
    taxSettings: input.taxSettings
      ? {
          showCategories: input.taxSettings.showCategories,
          displayMode: input.taxSettings.displayMode as 'hidden' | 'simple' | 'detailed',
          showItemTaxDetail: input.taxSettings.showItemTaxDetail,
        }
      : undefined,
  };
};
